/*!
 * opus-horizon-cart-submit-fix.js
 * ------------------------------------------------------------------
 * Prevents duplicate cart additions when Shopify's Horizon theme and
 * EGO/Opus Cart Drawer both handle the same product-form submission.
 *
 * Horizon delegates `on:submit="/handleSubmit"` from the document in the
 * capture phase. Opus also intercepts the form and posts to cart/add. Because
 * Horizon runs first, Opus cannot cancel the already-started Horizon request.
 * Removing only Horizon's declarative submit attribute leaves Opus as the
 * single cart owner and preserves its drawer and upsells. The Opus cart state
 * is also mirrored to Horizon's header cart count without dispatching
 * `cart:update`, which would open Horizon's native drawer over the Opus drawer.
 *
 * The change activates only after the Opus shadow-root drawer exists. It also
 * covers forms added or re-rendered later by Horizon.
 *
 * Added 2026-09-02 by Connected Systems.
 */
(function () {
  'use strict';

  var HOST_SELECTOR = '#opus-shadow-container';
  var DRAWER_SELECTOR = '#opus-drawer-id';
  var FORM_COMPONENT_SELECTOR = 'product-form-component[on\\:submit="/handleSubmit"]';
  var MARKER_ATTRIBUTE = 'data-cs-opus-submit-fix';
  var POLL_MS = 100;
  var POLL_LIMIT = 150; // ~15s, then leave Horizon untouched

  function getOpusRoot() {
    var host = document.querySelector(HOST_SELECTOR);
    return host && host.shadowRoot ? host.shadowRoot : null;
  }

  function syncHorizonCartCount(root) {
    var cartIcon = document.querySelector('cart-icon');
    if (!cartIcon || typeof cartIcon.renderCartBubble !== 'function') return;

    var count = 0;
    root.querySelectorAll('.cd-product-item .cd-number-input-field').forEach(function (input) {
      count += Number.parseInt(input.value, 10) || 0;
    });

    cartIcon.renderCartBubble(count, false, false);
  }

  function disableHorizonSubmit(root) {
    if (!root || !root.querySelectorAll) return;

    var components = [];
    if (root.matches && root.matches(FORM_COMPONENT_SELECTOR)) components.push(root);
    root.querySelectorAll(FORM_COMPONENT_SELECTOR).forEach(function (component) {
      components.push(component);
    });

    components.forEach(function (component) {
      component.removeAttribute('on:submit');
      component.setAttribute(MARKER_ATTRIBUTE, '1');
    });
  }

  function attach() {
    var opusRoot = getOpusRoot();
    if (!opusRoot || !opusRoot.querySelector(DRAWER_SELECTOR)) return false;

    disableHorizonSubmit(document);
    syncHorizonCartCount(opusRoot);

    var syncTimer = null;
    function scheduleCartCountSync() {
      clearTimeout(syncTimer);
      syncTimer = setTimeout(function () {
        syncHorizonCartCount(opusRoot);
      }, 60);
    }

    new MutationObserver(scheduleCartCountSync).observe(opusRoot, {
      childList: true,
      subtree: true
    });
    opusRoot.addEventListener('input', scheduleCartCountSync, true);
    opusRoot.addEventListener('change', scheduleCartCountSync, true);

    new MutationObserver(function (mutations) {
      mutations.forEach(function (mutation) {
        if (mutation.type === 'attributes') {
          disableHorizonSubmit(mutation.target);
          return;
        }

        mutation.addedNodes.forEach(function (node) {
          if (node.nodeType === Node.ELEMENT_NODE) disableHorizonSubmit(node);
        });
      });
    }).observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['on:submit'],
      childList: true,
      subtree: true
    });

    return true;
  }

  if (!attach()) {
    var tries = 0;
    var poll = setInterval(function () {
      if (attach() || ++tries > POLL_LIMIT) clearInterval(poll);
    }, POLL_MS);
  }
})();
